import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { services, comboPackages } from '../mock';

const PricingTabs = ({ currentService = null, showAllServices = true, onCtaClick }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(currentService ? currentService.slug : 'combo-packages');
  const [slideDirection, setSlideDirection] = useState('right');

  const handleCheckout = (packageData) => {
    navigate('/checkout', { state: { package: packageData } });
  };

  // Create tabs array - when showing specific service, only show that service's pricing
  const tabs = showAllServices 
    ? [
        { id: 'combo-packages', label: 'Combo Packages', type: 'combo' },
        ...services.map(service => ({
          id: service.slug,
          label: service.title,
          type: 'service',
          data: service
        }))
      ]
    : [
        { id: currentService.slug, label: currentService.title, type: 'service', data: currentService }
      ];

  // Set active tab based on context
  const displayTabs = tabs;

  const handleTabClick = (tabId) => {
    // Determine slide direction
    const currentIndex = displayTabs.findIndex(tab => tab.id === activeTab);
    const newIndex = displayTabs.findIndex(tab => tab.id === tabId);
    setSlideDirection(newIndex > currentIndex ? 'right' : 'left');
    setActiveTab(tabId);
  };

  return (
    <div className="w-full">
      {/* Tabs Navigation */}
      <div className="flex overflow-x-auto pb-4 mb-12 hide-scrollbar">
        <div className="flex flex-wrap gap-2 justify-center w-full px-4">
          {displayTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`px-6 py-3 rounded-xl font-semibold text-sm whitespace-nowrap transition-all duration-300 transform hover:scale-105 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs Content */}
      <div className="relative" style={{ minHeight: '480px' }}>
        {/* Combo Packages Tab */}
        <div className={`${activeTab === 'combo-packages' ? 'block' : 'hidden'}`}>
          <div 
            key="combo-packages"
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 ${
              activeTab === 'combo-packages' ? (slideDirection === 'right' ? 'animate-slideInRight' : 'animate-slideInLeft') : ''
            }`}
          >
            {comboPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 hover:-translate-y-2 flex flex-col ${
                  pkg.popular
                    ? 'border-purple-500 md:scale-105 relative'
                    : 'border-gray-200'
                }`}
                style={{ height: '480px' }}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-5 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg animate-pulse whitespace-nowrap">
                    <Star size={12} fill="currentColor" />
                    MOST POPULAR
                  </div>
                )}
                <div className={`text-center mb-4 ${pkg.popular ? 'mt-2' : ''}`}>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">{pkg.name}</h3>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    {pkg.price}
                  </div>
                  <p className="text-gray-600 text-xs">{pkg.description}</p>
                  {pkg.savings && (
                    <div className="mt-2 inline-block bg-green-100 text-green-700 px-3 py-0.5 rounded-full text-xs font-semibold">
                      {pkg.savings}
                    </div>
                  )}
                </div>

                {/* Scrollable Features */}
                <div className="flex-grow overflow-y-auto mb-4 pr-2 custom-scrollbar" style={{ maxHeight: '240px' }}>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} strokeWidth={3} />
                        <span className="text-gray-700 text-xs leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleCheckout(pkg)}
                  className={`w-full text-center px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Individual Service Tabs */}
        {services.map((service) => (
          <div 
            key={service.slug}
            className={`${activeTab === service.slug ? 'block' : 'hidden'}`}
          >
            <div 
              className={`grid grid-cols-1 md:grid-cols-3 gap-8 ${
                activeTab === service.slug ? (slideDirection === 'right' ? 'animate-slideInRight' : 'animate-slideInLeft') : ''
              }`}
            >
              {/* Basic Plan */}
              <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:-translate-y-2 flex flex-col" style={{ height: '480px' }}>
                <div className="text-center mb-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">Basic</h3>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    {service.pricing.basic.price}
                  </div>
                  <p className="text-gray-600 text-xs">Perfect for getting started</p>
                </div>
                
                {/* Scrollable Features */}
                <div className="flex-grow overflow-y-auto mb-4 pr-2 custom-scrollbar" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    {service.pricing.basic.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} strokeWidth={3} />
                        <span className="text-gray-700 text-xs leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button
                  onClick={() => handleCheckout({ 
                    ...service.pricing.basic, 
                    name: `${service.title} - Basic`,
                    description: service.description,
                    serviceType: service.title
                  })}
                  className="w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  Get Started
                </button>
              </div>

              {/* Standard Plan */}
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-5 shadow-2xl relative flex flex-col md:scale-105" style={{ height: '480px' }}>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-5 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-pulse whitespace-nowrap">
                  <Star size={12} fill="currentColor" />
                  POPULAR
                </div>
                <div className="text-center mb-4 mt-2">
                  <h3 className="text-lg md:text-xl font-bold text-white mb-1">Standard</h3>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                    {service.pricing.standard.price}
                  </div>
                  <p className="text-white/90 text-xs">Best value for businesses</p>
                </div>
                
                {/* Scrollable Features */}
                <div className="flex-grow overflow-y-auto mb-4 pr-2 custom-scrollbar" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    {service.pricing.standard.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="text-white flex-shrink-0 mt-0.5" size={16} strokeWidth={3} />
                        <span className="text-white text-xs leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button
                  onClick={() => handleCheckout({ 
                    ...service.pricing.standard, 
                    name: `${service.title} - Standard`,
                    description: service.description,
                    serviceType: service.title
                  })}
                  className="w-full text-center bg-white text-purple-600 px-5 py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  Get Started
                </button>
              </div>

              {/* Premium Plan */}
              <div className="bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:-translate-y-2 flex flex-col" style={{ height: '480px' }}>
                <div className="text-center mb-4">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">Premium</h3>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    {service.pricing.premium.price}
                  </div>
                  <p className="text-gray-600 text-xs">For enterprise needs</p>
                </div>
                
                {/* Scrollable Features */}
                <div className="flex-grow overflow-y-auto mb-4 pr-2 custom-scrollbar" style={{ maxHeight: '280px' }}>
                  <ul className="space-y-2">
                    {service.pricing.premium.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} strokeWidth={3} />
                        <span className="text-gray-700 text-xs leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button
                  onClick={() => handleCheckout({ 
                    ...service.pricing.premium, 
                    name: `${service.title} - Premium`,
                    description: service.description,
                    serviceType: service.title
                  })}
                  className="w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-3 rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingTabs;
